import { createContext } from '@nucleus/react-components';
import { PostIndexDocumentWeb } from '@nucleus/web-hosting';
import { flatMap as _flatMap, get as _get } from 'lodash';
import React from 'react';
import { usePostPath } from '../hooks/usePostPath';
import { usePostSearch } from '../hooks/usePostSearch';

type UsePostSearch = ReturnType<typeof usePostSearch>;

type PostSearchContext = Pick<UsePostSearch, 'status' | 'hasNextPage' | 'fetchNextPage' | 'isFetchingNextPage'> & {
  getPostPath: (post: PostIndexDocumentWeb) => string;
  limit: number;
  searchQuery: string;
  posts: PostIndexDocumentWeb[];
  setSearchQuery: (searchQuery: string) => void;
  total: number;
};

const [PostSearchProvider, usePostSearchController] = createContext<PostSearchContext>({
  name: 'PostSearchContext',
});

export { usePostSearchController };

interface PostSearchControllerProps extends useControllerProps {
  children: React.ReactNode;
}
export const PostSearchController = ({ children, ...props }: PostSearchControllerProps): JSX.Element => {
  return <PostSearchProvider value={useController(props)}>{children}</PostSearchProvider>;
};

interface useControllerProps {
  value: string;
  onChange: (value: string) => void;
  basePath: string;
  engineId: string;
  limit?: number;
}
const useController = ({ value, onChange, limit = 6, ...options }: useControllerProps): PostSearchContext => {
  const { data, status, hasNextPage, fetchNextPage, isFetchingNextPage } = usePostSearch(options.engineId, {
    query: value,
    limit: limit,
  });

  const getPostPath = usePostPath(options.basePath);

  return {
    fetchNextPage: fetchNextPage,
    getPostPath: getPostPath,
    hasNextPage: hasNextPage,
    isFetchingNextPage: isFetchingNextPage,
    limit: limit,
    searchQuery: value,
    posts: _flatMap(data?.pages, 'posts'),
    setSearchQuery: onChange,
    status: status,
    total: _get(data, 'pages[0].total', 0),
  };
};
